import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BoxedImage, Crop } from '../../../components/BoxedImage';
import { EmbeddingsLink } from '../../../components/EmbeddingsLink';
import { ZoomableImage } from '../../../components/ZoomableImage';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ApiService } from '../../../services/api';
import {
  CameraImageResponse,
  CropEmbeddingSearchResponse,
  PlanogramComplianceResponse,
  ProductRecognitionBBox,
  ProductRecognitionPolygon,
} from '../../../types';
import { getConfidenceClass, mapOcrConfidenceToLabel } from '../../../utils/confidenceUtils';
import { findExpectedPogCandidate } from '../../../utils/findExpectedPogItem';

interface ProductRecognitionDetectionContentProps {
  item: ProductRecognitionBBox | ProductRecognitionPolygon;
  cameraImageElement?: HTMLImageElement;
  layerBboxColor?: string;
  chainId?: number;
  storeId?: number;
  onUpcSubmit?: (upc: string) => void;
  planogramComplianceData?: PlanogramComplianceResponse | null;
  cameraImage?: CameraImageResponse | null;
  cartonBoxes?: ProductRecognitionBBox[] | null;
  withReferenceCrops?: boolean;
}

export const ProductRecognitionDetectionContent: React.FC<
  ProductRecognitionDetectionContentProps
> = ({
  item,
  cameraImageElement,
  layerBboxColor,
  chainId,
  storeId,
  onUpcSubmit,
  planogramComplianceData,
  cameraImage,
  cartonBoxes,
  withReferenceCrops = false,
}) => {
  const [zoomedImage, setZoomedImage] = useState<HTMLImageElement | null>(null);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState<number | null>(null);
  const [referenceCropDetails, setReferenceCropDetails] = useState<
    Record<string, CropEmbeddingSearchResponse>
  >({});
  const [embeddingActiveOverrides, setEmbeddingActiveOverrides] = useState<Record<string, boolean>>(
    {}
  );
  const [embeddingPendingByCrop, setEmbeddingPendingByCrop] = useState<Record<string, boolean>>({});

  const { token } = useAuthContext();
  const { showSuccessToast, showErrorToast } = useNotificationContext();
  const [upcInput, setUpcInput] = useState('');

  const handleToggleEmbeddingActive = async (cropId: string, currentIsActive: boolean) => {
    if (!token || embeddingPendingByCrop[cropId]) return;
    const newState = !currentIsActive;
    setEmbeddingPendingByCrop((prev) => ({ ...prev, [cropId]: true }));
    setEmbeddingActiveOverrides((prev) => ({ ...prev, [cropId]: newState }));
    try {
      await ApiService.updateEmbeddingActiveState(token, cropId, newState);
      showSuccessToast(`Embedding ${newState ? 'activated' : 'deactivated'}`);
    } catch {
      // revert on failure
      setEmbeddingActiveOverrides((prev) => ({ ...prev, [cropId]: currentIsActive }));
      showErrorToast(`Failed to ${newState ? 'activate' : 'deactivate'} embedding`);
    } finally {
      setEmbeddingPendingByCrop((prev) => ({ ...prev, [cropId]: false }));
    }
  };

  // Use the provided HTMLImageElement directly
  const cropImage = cameraImageElement;

  const isObstruction = 'pngData' in item && !!(item as any).pngData?.b64;

  // Use pre-calculated crop coordinates from item.shape
  // For obstructions (PNG mask, no shape), use full image dimensions
  const cropCoordinates = useMemo((): Crop | undefined => {
    if (!cropImage) return undefined;
    if (isObstruction) return [0, 0, cropImage.naturalWidth, cropImage.naturalHeight];
    if (!item.shape) return undefined;
    return item.shape;
  }, [item.shape, cropImage, isObstruction]);

  // Find expected POG candidate and merge with existing candidates
  const candidatesWithExpectedPog = useMemo(() => {
    const existingCandidates = item.vizSimCandidates || [];

    // Find expected POG candidate
    const expectedPogCandidate = findExpectedPogCandidate(
      item,
      planogramComplianceData || null,
      cameraImage || null,
      cartonBoxes
    );

    if (!expectedPogCandidate) {
      return existingCandidates;
    }

    // Check if expected POG UPC already exists in candidates
    const existingIndex = existingCandidates.findIndex(
      (candidate) =>
        candidate.normalizedBarcode === expectedPogCandidate.normalizedBarcode ||
        ((expectedPogCandidate.normalizedBarcode &&
          candidate.allNormalizedBarcodes?.includes(expectedPogCandidate.normalizedBarcode)) ??
          false)
    );

    if (existingIndex !== -1) {
      // Mark existing candidate as expected POG
      const updatedCandidates = [...existingCandidates];
      updatedCandidates[existingIndex] = {
        ...updatedCandidates[existingIndex],
        isExpectedPog: true,
      };
      return updatedCandidates;
    }

    return [...existingCandidates, expectedPogCandidate];
  }, [item, planogramComplianceData, cameraImage, cartonBoxes]);

  const handleCandidateSelected = onUpcSubmit
    ? (candidate: any) => {
        if (candidate.normalizedBarcode) {
          setUpcInput(candidate.normalizedBarcode);
        }
      }
    : undefined;

  const handleUpcSubmit = () => {
    if (onUpcSubmit) {
      onUpcSubmit(upcInput);
      setUpcInput('');
    }
  };

  // Fetch details for all reference crop ids found across candidates
  useEffect(() => {
    const allCropIds = candidatesWithExpectedPog.flatMap(
      (c) => c.referenceCrops?.map((rc) => rc.embeddingCropId) ?? []
    );
    const uniqueCropIds = [...new Set(allCropIds)];
    if (!withReferenceCrops || !token || uniqueCropIds.length === 0) return;

    ApiService.searchEmbeddingsByCropIds(token, uniqueCropIds)
      .then((results) => {
        const byId: Record<string, CropEmbeddingSearchResponse> = {};
        results.forEach((r) => {
          if (r.productEmbeddingCropId) {
            byId[r.productEmbeddingCropId] = r;
          }
        });
        setReferenceCropDetails(byId);
      })
      .catch(() => {
        // silently ignore fetch errors for reference crops
      });
  }, [candidatesWithExpectedPog, withReferenceCrops, token]);

  return (
    <>
      {/* First Row: Crop Image + Details */}
      <div className="flex gap-4 mb-4">
        {/* Left: Cropped Camera Image */}
        <div className="flex-shrink-0 w-48">
          {isObstruction ? (
            <div>
              <span className="text-gray-600 block mb-2 text-xs">Obstruction Mask:</span>
              <div
                className="w-full overflow-hidden rounded border cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => cameraImageElement && setZoomedImage(cameraImageElement)}
              >
                <img
                  src={`data:image/png;base64,${(item as any).pngData.b64}`}
                  alt="Obstruction mask"
                  className="w-full"
                />
              </div>
            </div>
          ) : (
            cropImage &&
            cropCoordinates && (
              <div>
                <span className="text-gray-600 block mb-2 text-xs">Detected Area:</span>
                <div
                  className="w-full overflow-hidden rounded border cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => cameraImageElement && setZoomedImage(cameraImageElement)}
                >
                  <BoxedImage
                    image={cropImage}
                    crop={cropCoordinates}
                    cropPadding={20}
                    boxes={[
                      {
                        shape: cropCoordinates,
                        color: layerBboxColor || '#3b82f6',
                        isPolygon: 'points' in item,
                        strokeWidth: 5,
                        points: 'points' in item ? item.points : undefined,
                      },
                    ]}
                  />
                </div>
              </div>
            )
          )}
        </div>

        {/* Right: Details */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 gap-2">
            {'x1' in item && (
              <>
                <span className="text-gray-600">Coordinates:</span>
                <span className="font-mono">
                  ({item.x1}, {item.y1}) - ({item.x2}, {item.y2})
                </span>
              </>
            )}
            {'points' in item && item.points && (
              <>
                <span className="text-gray-600">Points:</span>
                <span className="font-mono">{item.points.length} vertices</span>
              </>
            )}
            {item.type && (
              <>
                <span className="text-gray-600">Type:</span>
                <span>{item.type}</span>
              </>
            )}
            {item.confidence && (
              <>
                <span className="text-gray-600">Confidence:</span>
                <span>{(item.confidence * 100).toFixed(1)}%</span>
              </>
            )}
            {item.column !== undefined && (
              <>
                <span className="text-gray-600">Column:</span>
                <span>{item.column}</span>
              </>
            )}
            {item.mod !== undefined && (
              <>
                <span className="text-gray-600">Mod:</span>
                <span>{item.mod}</span>
              </>
            )}
            {item.shelf !== undefined && (
              <>
                <span className="text-gray-600">Shelf:</span>
                <span>{item.shelf}</span>
              </>
            )}
            {item.ocr?.text && (
              <>
                <span className="text-gray-600">OCR Text:</span>
                <span>"{item.ocr.text}"</span>
              </>
            )}
            {item.ocr?.confidence !== undefined && (
              <>
                <span className="text-gray-600">OCR Confidence:</span>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceClass(
                    item.ocr.confidence
                  )}`}
                >
                  {mapOcrConfidenceToLabel(item.ocr.confidence)}
                </span>
              </>
            )}
            {item.vizSimCandidates?.[0]?.normalizedBarcode && (
              <>
                <span className="text-gray-600">Final UPC:</span>
                <span>{item.vizSimCandidates[0].normalizedBarcode}</span>
              </>
            )}
            {/* UPC Input Section */}
            {onUpcSubmit && (
              <div className="mt-6 flex items-center gap-3">
                <input
                  type="text"
                  value={upcInput}
                  onChange={(e) => setUpcInput(e.target.value)}
                  placeholder="Correct UPC"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  disabled={!upcInput.trim()}
                  onClick={handleUpcSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400"
                >
                  Submit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Second Row: Similar Products */}
      {candidatesWithExpectedPog && candidatesWithExpectedPog.length > 0 && (
        <div>
          <span className="text-gray-600 block mb-3 font-medium">Similar Products:</span>
          {/* Row 1: Candidate cards */}
          <div className="flex gap-3 overflow-x-auto pb-1">
            {candidatesWithExpectedPog.slice(0, 10).map((candidate, idx) => {
              return (
                <div key={idx} className="flex-shrink-0 w-56">
                  {/* Candidate card */}
                  <div
                    onClick={() => {
                      if (onUpcSubmit && handleCandidateSelected) {
                        setSelectedCandidateIndex(idx);
                        handleCandidateSelected(candidate);
                      }
                    }}
                    className={`p-3 rounded border text-xs transition-colors relative group ${
                      onUpcSubmit ? 'cursor-pointer hover:shadow-md' : ''
                    } ${
                      candidate.isExpectedPog
                        ? 'bg-purple-50 border-purple-400 border-2'
                        : selectedCandidateIndex === idx
                          ? 'bg-blue-50 border-blue-300'
                          : candidate.isConfident
                            ? 'bg-green-50 border-green-200'
                            : 'bg-white border-gray-200'
                    }`}
                  >
                    {/* Expected POG Badge */}
                    {candidate.isExpectedPog && (
                      <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                        Expected POG
                      </div>
                    )}
                    {/* Product Image - Larger and clickable */}
                    <div className="mb-2">
                      {candidate.productImageUri ? (
                        <img
                          src={candidate.productImageUri}
                          alt={candidate.productName || candidate.normalizedBarcode || ''}
                          className="w-full h-48 object-contain rounded cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (candidate.productImageUri) {
                              const img = new Image();
                              img.src = candidate.productImageUri;
                              img.onload = () => setZoomedImage(img);
                            }
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-24 bg-gray-200 rounded flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div>
                      {/* Product Name - Bolded and wrapped */}
                      <div className="font-bold text-gray-900 text-xs leading-tight mb-1 line-clamp-2">
                        {candidate.productName || 'Unknown Product'}
                      </div>

                      {/* Normalized UPC */}
                      <div
                        className={`font-mono text-xs mb-1 ${
                          candidate.isActive === false ? 'text-orange-600' : 'text-gray-600'
                        }`}
                        title={
                          candidate.allNormalizedBarcodes &&
                          candidate.allNormalizedBarcodes.length > 1
                            ? candidate.allNormalizedBarcodes.join(', ')
                            : candidate.normalizedBarcode || ''
                        }
                      >
                        {candidate.allNormalizedBarcodes &&
                        candidate.allNormalizedBarcodes.length > 1
                          ? candidate.allNormalizedBarcodes.join(', ')
                          : candidate.normalizedBarcode}
                        {candidate.isActive === false && (
                          <span className="ml-1 font-sans font-medium">• Inactive</span>
                        )}
                      </div>

                      {/* Item Number and Product ID on single line */}
                      {(candidate.itemNumber || candidate.productId) && (
                        <div className="text-gray-500 text-xs mb-1 truncate">
                          {candidate.itemNumber && `Item: ${candidate.itemNumber}`}
                          {candidate.itemNumber && candidate.productId && ' • '}
                          {candidate.productId && `ID: ${candidate.productId}`}
                        </div>
                      )}

                      {/* Similarity Level and Embeddings Link */}
                      <div className="flex items-center justify-between">
                        <div
                          className={`text-xs flex items-center gap-1 ${
                            candidate.isExpectedPog
                              ? 'text-purple-700'
                              : candidate.isConfident
                                ? 'text-green-700'
                                : 'text-gray-600'
                          }`}
                        >
                          <span className="font-medium">
                            {candidate.similarity !== null
                              ? (candidate.similarity * 100).toFixed(1) + '%'
                              : ''}
                          </span>
                          {candidate.isConfident && (
                            <svg
                              className={`w-3 h-3 ${
                                candidate.isExpectedPog ? 'text-purple-600' : 'text-green-600'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>

                        {/* Embeddings Link */}
                        {chainId && (candidate.normalizedBarcode || candidate.itemNumber) && (
                          <EmbeddingsLink
                            upc={candidate.normalizedBarcode ?? ''}
                            chainId={chainId}
                            storeId={storeId ?? (item as any).storeId}
                            itemNumber={candidate.itemNumber}
                            variant="link"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Row 2: Reference crops — one column per candidate, empty if none */}
          <div className="flex gap-3 overflow-x-auto pt-1 pb-2">
            {candidatesWithExpectedPog.slice(0, 10).map((candidate, idx) => {
              const candidateReferenceCrops = (candidate.referenceCrops ?? [])
                .map((rc) => ({ rc, detail: referenceCropDetails[rc.embeddingCropId] }))
                .filter(({ detail }) => !!detail);
              return (
                <div key={idx} className="flex-shrink-0 w-56">
                  {candidateReferenceCrops.length > 0 && (
                    <div className="bg-gray-50 rounded border border-gray-200 p-2">
                      <span className="text-gray-500 text-xs block mb-1">Reference crops:</span>
                      <div className="flex flex-col divide-y divide-gray-300">
                        {candidateReferenceCrops.map(({ rc, detail }) => {
                          const params = new URLSearchParams();
                          if (chainId) params.append('chain_id', chainId.toString());
                          if (storeId) params.append('store_id', storeId.toString());
                          if (detail.itemNumber) {
                            params.append('item_number', detail.itemNumber.toString());
                          } else if (detail.normalizedBarcode) {
                            params.append('upc', detail.normalizedBarcode);
                          }
                          params.append('crop_id', rc.embeddingCropId);
                          const isActive =
                            rc.embeddingCropId in embeddingActiveOverrides
                              ? embeddingActiveOverrides[rc.embeddingCropId]
                              : detail.isActive;
                          return (
                            <div
                              key={rc.embeddingCropId}
                              className="rounded px-1 py-3 hover:bg-gray-100 transition-colors group flex justify-between gap-6"
                            >
                              <ZoomableImage
                                src={detail.vizUrl}
                                alt="Reference crop"
                                className="w-full object-contain rounded bg-white border border-gray-200"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                              <div className="flex flex-col justify-between items-end">
                                <button
                                  title={isActive ? 'Deactivate embedding' : 'Activate embedding'}
                                  disabled={!!embeddingPendingByCrop[rc.embeddingCropId]}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleEmbeddingActive(rc.embeddingCropId, isActive);
                                  }}
                                  className="p-2 rounded hover:bg-gray-200 transition-opacity opacity-20 group-hover:opacity-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {isActive ? (
                                    <BlockOutlinedIcon
                                      sx={{ fontSize: 24, color: 'rgb(239 68 68)' }}
                                    />
                                  ) : (
                                    <CheckCircleOutlinedIcon
                                      sx={{ fontSize: 24, color: 'rgb(34 197 94)' }}
                                    />
                                  )}
                                </button>

                                <span className="text-gray-700 text-xs font-medium tabular-nums">
                                  {(rc.similarity * 100).toFixed(1)}%
                                </span>
                                <Link
                                  to={`/embeddings/detail?${params.toString()}`}
                                  className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-gray-200 opacity-0 group-hover:opacity-100"
                                  target="_blank"
                                  title="Go to embeddings"
                                >
                                  <LaunchIcon sx={{ fontSize: 26 }} />
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {zoomedImage === cameraImageElement && cropCoordinates ? (
              // Show camera image with detected box highlighted
              (() => {
                const maxWidth = window.innerWidth * 0.9;
                const maxHeight = window.innerHeight * 0.9;
                const imageAspectRatio = zoomedImage.naturalWidth / zoomedImage.naturalHeight;
                const containerAspectRatio = maxWidth / maxHeight;

                let displayWidth, displayHeight;
                if (imageAspectRatio > containerAspectRatio) {
                  // Image is wider than container
                  displayWidth = Math.min(maxWidth, zoomedImage.naturalWidth);
                  displayHeight = displayWidth / imageAspectRatio;
                } else {
                  // Image is taller than container
                  displayHeight = Math.min(maxHeight, zoomedImage.naturalHeight);
                  displayWidth = displayHeight * imageAspectRatio;
                }

                return (
                  <div
                    className="bg-white rounded shadow-lg"
                    style={{
                      width: `${displayWidth}px`,
                      height: `${displayHeight}px`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BoxedImage
                      image={zoomedImage}
                      boxes={[
                        {
                          shape: cropCoordinates,
                          color: layerBboxColor || '#3b82f6',
                          strokeWidth: isObstruction ? 0 : 10,
                          isPolygon: !isObstruction && 'points' in item,
                          points: !isObstruction && 'points' in item ? item.points : undefined,
                          maskImage: isObstruction ? (item as any).pngData.b64 : undefined,
                        },
                      ]}
                    />
                  </div>
                );
              })()
            ) : (
              // Show regular product image
              <img
                src={zoomedImage.src}
                alt="Zoomed product"
                className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-lg"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 transition-all shadow-lg"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
